import { useState } from "react";

/**
 * A closing date, as a calendar rather than a hand-typed ISO string.
 *
 * 🔴 THE THREE WAYS THE BOX WAS WRONG AND LOOKED RIGHT: a missing `T`, a local time entered as
 * if it were UTC, and a month and day the wrong way round. None of them is visible in
 * `2026-10-01T17:00:00`, and the first is refused only on save.
 *
 * 🔴 IT STILL WRITES AN ISO STRING. Nothing changes server-side — `projects.deadline()` reads
 * it, and a value with no zone is read as UTC, which is what this control now says out loud
 * instead of leaving in a hint.
 *
 * ⚠️ EMPTY READS `No deadline`, NOT BLANK. Blank looks unset; empty is a valid answer, and the
 * two are worth telling apart on a control that governs whether a form is open.
 *
 * ⚠️ A PAST DATE CLOSES AN OPEN FORM IMMEDIATELY, so it is called out before saving rather
 * than discovered by a student who can reach the form right now. It is not refused: back-dating
 * a deadline is a legitimate way to shut a round.
 */
export function Deadline({ value, disabled, onChange }: {
  value: string; disabled: boolean; onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const parsed = parse(value);
  const [month, setMonth] = useState(() => startOfMonth(parsed ?? new Date()));
  const [time, setTime] = useState(() => parsed ? hhmm(parsed) : "17:00");

  // 🔴 OPENING RE-SEEDS FROM THE VALUE, AND CANCEL MEANS CANCEL. [X] `month` and `time` were
  // `useState` initialisers and nothing ever restored them, so `Cancel` after editing the
  // time left the edited time in state — and the NEXT day you clicked, days later, committed
  // the time you had cancelled. Same for a value somebody else changed underneath you.
  const reopen = () => {
    setMonth(startOfMonth(parsed ?? new Date()));
    setTime(parsed ? hhmm(parsed) : "17:00");
    setOpen(true);
  };

  // 🔴 A TIME THAT IS NOT A TIME IS REFUSED, NOT COERCED. [X] Measured: an empty box became
  // `Number("") = 0` and committed **00:00**, seventeen hours before the control's own
  // default; `99:99` overflowed into `Date.UTC` and moved the DATE four days from the one
  // that was clicked, with the popover closing on commit so the two were never seen
  // together; and a large enough value threw `RangeError` inside the click handler, which
  // `ErrorBoundary` turns into a replaced page and a lost draft.
  const t = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time.trim());
  const commit = (day: Date) => {
    if (!t) return;
    const d = new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(),
                                Number(t[1]), Number(t[2])));
    // The `Z` is not decoration: it is the difference between "17:00 somewhere" and the
    // instant every reader agrees on.
    onChange(d.toISOString().replace(/\.\d{3}Z$/, "Z"));
    setOpen(false);
  };

  return (
    <div className="dl">
      <button type="button" className="dl-btn" disabled={disabled}
              aria-expanded={open} onClick={() => (open ? setOpen(false) : reopen())}>
        <span>{parsed ? human(parsed) : "No deadline"}</span>
        {parsed && <span className="dl-zone">UTC</span>}
      </button>
      {parsed && <p className={past(parsed) ? "dl-rel warn" : "dl-rel"}>{relative(parsed)}</p>}

      {open && !disabled && (
        <div className="dl-pop">
          <div className="dl-head">
            <button type="button" aria-label="Previous month"
                    onClick={() => setMonth(shift(month, -1))}>‹</button>
            <span>{month.toLocaleString("en-GB",
                    { month: "long", year: "numeric", timeZone: "UTC" })}</span>
            <button type="button" aria-label="Next month"
                    onClick={() => setMonth(shift(month, 1))}>›</button>
          </div>
          <div className="dl-grid" role="grid">
            {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
              <span key={i} className="dl-dow">{d}</span>))}
            {cells(month).map((d, i) => d === null
              ? <span key={i} />
              : (
                <button key={i} type="button"
                        className={parsed && sameDay(d, parsed) ? "dl-day on" : "dl-day"}
                        disabled={!t} onClick={() => commit(d)}>{d.getUTCDate()}</button>))}
          </div>
          <div className="dl-time">
            <span className="kick">Time</span>
            <input className={t ? "tin" : "tin bad"} value={time} aria-label="Time, UTC"
                   placeholder="17:00" inputMode="numeric" maxLength={5}
                   onChange={(e) => setTime(e.target.value)} />
            <span className="dl-zone">UTC</span>
            {/* ⚠️ IT SAYS WHY THE DAYS ARE DEAD rather than letting a click do nothing. */}
            {!t && <span className="dl-bad">24-hour, like 17:00</span>}
          </div>
          <div className="dl-foot">
            <button type="button" className="btn ghost sm"
                    onClick={() => { onChange(""); setOpen(false); }}>No deadline</button>
            <span className="spacer" />
            <button type="button" className="btn ghost sm"
                    onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>)}
    </div>
  );
}

function parse(v: string): Date | null {
  if (!v.trim()) return null;
  // ⚠️ A NAIVE VALUE IS UTC HERE TOO, matching `projects.deadline()`. A control that read it
  // as local would show a different time from the one the server stores.
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(v) ? v : `${v}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

const startOfMonth = (d: Date) =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
const shift = (d: Date, by: number) =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + by, 1));
const sameDay = (a: Date, b: Date) => a.toISOString().slice(0, 10) === b.toISOString().slice(0, 10);
const hhmm = (d: Date) => d.toISOString().slice(11, 16);
const past = (d: Date) => d.getTime() < Date.now();

function human(d: Date): string {
  return d.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric",
                                     hour: "2-digit", minute: "2-digit", timeZone: "UTC" });
}

/** ⚠️ THE DIRECTION IS THE POINT. "6 days ago" on an open form means it is already shut. */
function relative(d: Date): string {
  const ms = d.getTime() - Date.now();
  // 🔴 DIRECTION BEFORE MAGNITUDE. [X] Rounding to days first reported a deadline earlier
  // TODAY as plain "TODAY" — no warning — for anything inside ±12 hours, which is exactly
  // the window somebody setting today's date is working in.
  if (ms < 0) {
    const days = Math.round(-ms / 86_400_000);
    return days === 0 ? "EARLIER TODAY — THIS CLOSES IT"
                      : `${days} DAY${days === 1 ? "" : "S"} AGO — THIS CLOSES IT`;
  }
  const days = Math.round(ms / 86_400_000);
  return days === 0 ? "LATER TODAY" : `IN ${days} DAY${days === 1 ? "" : "S"}`;
}

/** Monday-first, with leading blanks. Adjacent months are not offered: picking one is
 *  almost always a mis-click, and the arrows are right there. */
function cells(month: Date): (Date | null)[] {
  const first = startOfMonth(month);
  const lead = (first.getUTCDay() + 6) % 7;
  const days = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0))
    .getUTCDate();
  return [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: days }, (_, i) =>
      new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), i + 1))),
  ];
}
